import React from 'react';
import { CheckCircle, Clock, DollarSign, Calendar, User, CreditCard, Edit, Trash2 } from 'lucide-react';
import { formatCurrency, getPaymentMethodBadge } from '../hooks/useLoanRepayments.ts';

interface RepaymentTimelineProps {
  repayments: Array<{
    id: number;
    amount: number;
    payment_date: string;
    payment_method: string;
    description?: string;
    transaction_reference?: string;
    member?: {
      name: string;
    };
    processed_by_user?: {
      name: string;
    };
  }>;
  loanDetails?: {
    principal: number;
    loan_balance: number;
    total_amount: number;
    member?: {
      name: string;
    };
  };
  summary?: {
    total_repaid: number;
    repayment_count: number;
  };
  onEditRepayment?: (repayment: any) => void;
  onDeleteRepayment?: (repaymentId: number) => void;
  isAdmin?: boolean;
}

const LoanRepaymentTimeline: React.FC<RepaymentTimelineProps> = ({ 
  repayments, 
  loanDetails, 
  summary, 
  onEditRepayment, 
  onDeleteRepayment, 
  isAdmin = false 
}) => {
  const sortedRepayments = [...repayments].sort((a, b) => 
    new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
  );

  // Use summary data if available, fallback to local calculation
  const totalRepaid = summary?.total_repaid ?? repayments.reduce((sum, repayment) => sum + repayment.amount, 0);
  const progressPercentage = loanDetails ? (totalRepaid / loanDetails.total_amount) * 100 : 0;

  const getPaymentIcon = (method: string) => {
    switch (method) {
      case 'CASH': return <DollarSign size={16} className="text-green-600" />;
      case 'MPESA': return <CreditCard size={16} className="text-yellow-600" />;
      case 'BANK': return <CreditCard size={16} className="text-purple-600" />;
      case 'WALLET': return <CreditCard size={16} className="text-blue-600" />;
      default: return <CreditCard size={16} className="text-gray-600" />;
    }
  };

  if (repayments.length === 0) {
    return (
      <div className="bg-white rounded-lg p-6 text-center">
        <Clock size={48} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">No Repayments Yet</h3>
        <p className="text-gray-500">This loan hasn't received any repayments.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header with Progress */}
      {loanDetails && (
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Repayment Progress</h3>
              <p className="text-sm text-gray-600">
                {loanDetails.member?.name} • {formatCurrency(totalRepaid)} of {formatCurrency(loanDetails.total_amount)}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">{Math.round(progressPercentage)}%</div>
              <div className="text-sm text-gray-500">Complete</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            />
          </div>
          
          <div className="flex justify-between text-sm text-gray-600">
            <span>Remaining: {formatCurrency(loanDetails.loan_balance)}</span>
            <span>{repayments.length} payments made</span>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
          <Calendar size={18} className="mr-2" />
          Payment History
        </h4>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

          {/* Timeline Items */}
          <div className="space-y-6">
            {sortedRepayments.map((repayment, index) => (
              <div key={repayment.id} className="relative flex items-start group">
                {/* Timeline Dot */}
                <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center relative z-10 group-hover:bg-green-200 transition-colors">
                  <CheckCircle size={20} className="text-green-600" />
                </div>

                {/* Content */}
                <div className="ml-4 flex-1 bg-gray-50 rounded-lg p-4 group-hover:bg-gray-100 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-semibold text-gray-900">
                        {formatCurrency(repayment.amount)}
                      </span>
                      <div className="flex items-center space-x-1">
                        {getPaymentIcon(repayment.payment_method)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentMethodBadge(repayment.payment_method)}`}>
                          {repayment.payment_method}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                    {new Date(repayment.payment_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                    </div>
                  </div>

                  {repayment.description && (
                    <p className="text-sm text-gray-600 mb-2">{repayment.description}</p>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center space-x-4">
                      {repayment.transaction_reference && (
                        <span>Ref: {repayment.transaction_reference}</span>
                      )}
                      {repayment.processed_by_user && (
                        <div className="flex items-center">
                          <User size={12} className="mr-1" />
                          Processed by {repayment.processed_by_user.name}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-green-600 font-medium">
                        Payment #{sortedRepayments.length - index}
                      </span>
                      {isAdmin && (onEditRepayment || onDeleteRepayment) && (
                        <div className="flex items-center space-x-1 ml-2">
                          {onEditRepayment && (
                            <button
                              onClick={() => onEditRepayment(repayment)}
                              className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                              title="Edit repayment"
                            >
                              <Edit size={12} />
                            </button>
                          )}
                          {onDeleteRepayment && (
                            <button
                              onClick={() => onDeleteRepayment(repayment.id)}
                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                              title="Delete repayment"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Timeline End */}
          <div className="relative flex items-center mt-6">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center relative z-10">
              <DollarSign size={20} className="text-blue-600" />
            </div>
            <div className="ml-4 flex-1">
              <div className="text-sm text-gray-600 font-medium">
                Loan Started • {formatCurrency(loanDetails?.principal || 0)}
              </div>
              <div className="text-xs text-gray-500">
                Principal amount disbursed
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Footer */}
      <div className="px-6 py-4 bg-gray-50 rounded-b-lg">
        <div className="grid grid-cols-1 gap-4 text-center sm:grid-cols-3">
          <div>
            <div className="text-lg font-semibold text-gray-900">{repayments.length}</div>
            <div className="text-xs text-gray-500">Payments</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-green-600">{formatCurrency(totalRepaid)}</div>
            <div className="text-xs text-gray-500">Total Repaid</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-blue-600">
              {formatCurrency((totalRepaid / repayments.length) || 0)}
            </div>
            <div className="text-xs text-gray-500">Avg Payment</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanRepaymentTimeline; 